/**
 * @fileoverview Route guard for authenticated pages
 *
 * This component enforces access control by ensuring only authenticated users
 * can view protected routes. It handles redirecting unauthenticated users
 * to the login page while preserving their intended destination.
 *
 * Key Features:
 * - Uses Zustand store to verify auth state
 * - Displays a loading screen during auth check
 * - Redirects unauthenticated users to login
 * - Preserves return path for post-login navigation
 * - Renders protected content only if authenticated
 */

import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { LoadingScreen } from '@/components/loading-screen';
import { login } from '@/lib/auth';
import { USER_TYPE } from '@/lib/data';
import { useAuthStore } from '@/store/auth';

/**
 * Route guard component for protected pages
 *
 * This component wraps routes that require authentication and enforces the flow:
 * 1. Shows a loading screen while auth state is being verified
 * 2. Redirects unauthenticated users to the login page (with return path)
 * 3. Renders the protected route content if the user is authenticated
 *
 * @returns JSX element: loading screen, login redirect, or protected content
 *
 * @example
 * ```tsx
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 * ```
 */
export const ProtectedRoute = () => {
  // Component hooks
  const navigate = useNavigate();

  // Component stores
  const { user, isLoading, isAuthenticated } = useAuthStore();

  // Component side effect - navigate users to relevant page
  useEffect(() => {
    if (user?.userType === USER_TYPE.ADMIN) {
      navigate('/impersonation', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!isAuthenticated && !isLoading) {
    login();

    return <LoadingScreen />;
  }

  if (isLoading || user?.userType === USER_TYPE.ADMIN) {
    return <LoadingScreen />;
  }

  return <Outlet />;
};
